<template>
  <v-app>
    <transition name="fadeInUp" mode="out-in">
      <router-view />
    </transition>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from "vuex"; // Added back
import messaging from "@/plugins/firebase";

export default {
  name: "App",

  watch: {
    "notificationsData.unreadNotifications": {
      handler(val) {
        this.updateTitle(val);
      },
      immediate: true,
    },
    $route() {
      this.$nextTick(() => {
        this.updateTitle(this.notificationsData?.unreadNotifications);
      });
    }
  },

  computed: {
    // Start:: Vuex Getters
    ...mapGetters({
      getAuthenticatedUserData: "AuthenticationModule/getAuthenticatedUserData",
      notificationsData: "NotificationsModule/notificationsData",
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    // End:: Vuex Getters
  },

  data() {
    return {
      receivedMessages: [],
      notificationCount: 0,
      // Start:: Notifications Menu Control Data
      notificationsMenuIsOpen: false,
      // End:: Notifications Menu Control Data

      // Start:: User Profile Modal Controlling Data
      userProfileModalIsOpen: false,
      // End:: User Profile Modal Controlling Data
    };
  },

  methods: {
    // Start:: Vuex Actions
    ...mapActions({
      readAllNotifications: "NotificationsModule/readAllNotifications",
    }),
    // End:: Vuex Actions

    getNotificationRoute(message) {
      const type = message?.data?.type;
      const id = message?.data?.id;

      if (!type) return null;

      switch (type) {
        case "contact_us":
          return id ? `/contact-messages/show/${id}` : "/contact-messages/all";
        case "new_client":
          return id ? `/clients/show/${id}` : "/clients/all";
        case "new_printShop":
          return id ? `/providers/show/${id}` : "/providers/all";
        case "new_representative":
          return id ? `/representatives/show/${id}` : "/representatives/all";
        case "order":
        case "new_order":
        case "order_status":
        case "order_cancelled":
        case "order_assigned_to_representative":
          return id ? `/orders/show/${id}` : "/orders/all";
        case "wallet":
        case "wallet_clearance_request":
        case "wallet_top_up":
        case "wallet_clearance_accepted":
        case "wallet_clearance_rejected":
        case "wallet_balance_adjusted":
          return "/request-wallets/all";
        case "rating":
        case "rating_moderation":
        case "new_review":
          return "/reviews/all";
        case "delete_account":
          return "/clients/all";
        case "delete_representative":
          return "/representatives/all";
        case "delete_print_shop":
          return "/providers/all";
        default:
          return null;
      }
    },

    handleInAppNotification(payload, showDesktop = true) {
      if (payload && payload.notification) {
        const isRtl = this.getAppLocale === 'ar';
        
        // Extract localized content if available in data payload
        let title = payload.notification.title;
        let body = payload.notification.body;

        if (payload.data) {
           if (isRtl) {
              title = payload.data.title_ar || title;
              body = payload.data.body_ar || body;
           } else {
              title = payload.data.title_en || title;
              body = payload.data.body_en || body;
           }
        }

        // 1. Show Toast (In-App)
        this.$iziToast.info({
          title: title,
          message: body,
          timeout: 7000,
          rtl: isRtl,
          position: isRtl ? "topLeft" : "topRight",
          transitionIn: isRtl ? "fadeInRight" : "fadeInLeft",
          transitionOut: isRtl ? "fadeOutLeft" : "fadeOutRight",
          displayMode: "replace",
          onOpening: (instance, toast) => {
             toast.addEventListener('click', () => {
                const message = { data: payload.data }; 
                const route = this.getNotificationRoute(message);
                if (route) {
                   this.$router.push(route).catch(()=> {});
                   instance.hide({ transitionOut: 'fadeOut' }, toast, 'button');
                }
             });
             toast.style.cursor = 'pointer';
          }
        });

        // 2. Show Desktop Notification (System) - Only if requested
        if (showDesktop) {
           this.showDesktopNotification(payload);
        }
      }
    },

    showDesktopNotification(payload) {
      if (!("Notification" in window)) {
        return;
      }

      if (Notification.permission === "granted") {
        const isRtl = this.getAppLocale === 'ar';
        
        // Extract localized content if available in data payload
        let title = payload.notification.title;
        let body = payload.notification.body;

        if (payload.data) {
           if (isRtl) {
              title = payload.data.title_ar || title;
              body = payload.data.body_ar || body;
           } else {
              title = payload.data.title_en || title;
              body = payload.data.body_en || body;
           }
        }

        const notificationOption = {
          body: body,
          icon: '/favicon.ico',
          data: payload.data,
          dir: isRtl ? 'rtl' : 'ltr',
          lang: isRtl ? 'ar' : 'en'
        };
        const notification = new Notification(title, notificationOption);

        notification.onclick = (event) => {
          event.preventDefault();
          const message = { data: payload.data };
          const route = this.getNotificationRoute(message);
          if (route) {
            this.$router.push(route).catch(() => {});
            window.focus();
            notification.close();
          }
        };
      }
    },

    updateTitle(count) {
      const baseTitle = document.title.replace(/^\(\d+\)\s/, "");
      if (count > 0) {
        document.title = `(${count}) ${baseTitle}`;
      } else {
        document.title = baseTitle;
      }
    },
  },

  created() {
    // this.getData();
    this.readAllNotifications();

    // Handle Foreground Messages via Firebase SDK
    messaging.onMessage((payload) => {
      console.log("Message received (Foreground): ", payload);
      this.handleInAppNotification(payload, true); // Show Desktop Notification
      this.readAllNotifications();
    });

    // Handle Messages from Service Worker (Background -> Client)
    navigator.serviceWorker.addEventListener("message", (event) => {
      console.log("Message received (SW): ", event);
      const payload = event.data;
      // Do NOT show desktop notification, SW already did it
      this.handleInAppNotification(payload, false); 
      this.readAllNotifications();
    });
  },
};
</script>
